// 全局媒体库管理页。所有点位的图片/视频上传到这里,scene 详情页里的"段前媒体附件"
// 改成弹窗下拉从这里选,避免上下行/跨游线反复上传同一张图。
import { useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiSend, apiUpload, apiUrl } from "@/lib/api";
import { confirmDialog, toast } from "@/components/ui/feedback";

interface MediaAsset {
  id: string;
  type: "image" | "video";
  url: string;
  fileName: string;
  title: string;
  description: string;
  tags: string[];
  durationSec: number;
  sizeBytes: number;
  thumbUrl: string;
  usageCount: number;
  createdAt: string;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
function fmtDuration(sec: number): string {
  if (sec <= 0) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function MediaAssetsPage() {
  const [items, setItems] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<"" | "image" | "video">("");
  const [q, setQ] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [editing, setEditing] = useState<MediaAsset | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const replaceRef = useRef<HTMLInputElement>(null);
  const replaceTargetRef = useRef<string>("");

  const load = async () => {
    setLoading(true);
    try {
      const params: string[] = [];
      if (typeFilter) params.push(`type=${typeFilter}`);
      if (q.trim()) params.push(`q=${encodeURIComponent(q.trim())}`);
      const list = await apiGet<MediaAsset[]>(`/media-assets${params.length ? "?" + params.join("&") : ""}`);
      setItems(list ?? []);
    } catch (e) {
      toast("加载失败：" + (e as Error).message, "error");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter]);

  const onUpload = async (f: File) => {
    setUploadBusy(true);
    try {
      const res = await apiUpload<{ id: string; type: string }>("/media-assets", f);
      toast(`已上传 ${res.type === "video" ? "视频" : "图片"}`, "success");
      await load();
    } catch (e) {
      toast("上传失败：" + (e as Error).message, "error");
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onReplaceFile = async (f: File) => {
    const id = replaceTargetRef.current;
    if (!id) return;
    setUploadBusy(true);
    try {
      const res = await apiUpload<{ syncedSegments: number }>(`/media-assets/${id}/replace`, f);
      toast(`已替换${res?.syncedSegments ? `，同步更新 ${res.syncedSegments} 段讲解` : ""}`, "success");
      await load();
    } catch (e) {
      toast("替换失败：" + (e as Error).message, "error");
    } finally {
      setUploadBusy(false);
      replaceTargetRef.current = "";
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const onDelete = async (a: MediaAsset) => {
    const msg = a.usageCount > 0
      ? `该资源正被 ${a.usageCount} 段讲解音频引用，删除会一并把这些段的媒体清空。\n确认删除？`
      : "确认删除该资源？删除后无法恢复。";
    if (!await confirmDialog({ title: "删除媒体", message: msg, confirmText: "删除" })) return;
    try {
      await apiSend("DELETE", `/media-assets/${a.id}`);
      toast(a.usageCount > 0 ? `已删除并同步清空 ${a.usageCount} 段引用` : "已删除", "success");
      await load();
    } catch (e) {
      toast("删除失败：" + (e as Error).message, "error");
    }
  };

  const onSaveEdit = async (next: { title: string; description: string; tags: string[] }) => {
    if (!editing) return;
    try {
      await apiSend("PATCH", `/media-assets/${editing.id}`, next);
      toast("已保存", "success");
      setEditing(null);
      await load();
    } catch (e) {
      toast("保存失败：" + (e as Error).message, "error");
    }
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">媒体库</h1>
          <p className="text-xs text-gray-500 mt-1">所有点位共用的图片/视频资源池。讲解音频段从这里挑用,一处上传跨点位/游线复用。</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); }}
          />
          <input
            ref={replaceRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onReplaceFile(f); }}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploadBusy}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60"
          >
            <i className="ri-upload-2-line mr-1" />{uploadBusy ? "上传中…" : "上传图片或视频"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap bg-white border border-gray-200 rounded-lg p-3">
        <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
          {([
            ["", "全部"],
            ["image", "图片"],
            ["video", "视频"],
          ] as const).map(([v, label]) => (
            <button
              key={v}
              onClick={() => setTypeFilter(v)}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${typeFilter === v ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >{label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="按 标题 / 描述 / 标签 搜索（按回车）"
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>
        <button onClick={load} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
          搜索
        </button>
        <span className="text-xs text-gray-400 ml-auto">{items.length} 条</span>
      </div>

      {loading && <p className="text-center text-sm text-gray-400 py-8">加载中…</p>}
      {!loading && filtered.length === 0 && (
        <div className="border border-dashed border-gray-300 rounded-lg p-10 text-center">
          <i className="ri-image-2-line text-4xl text-gray-300 block mb-3" />
          <p className="text-sm text-gray-500">媒体库为空</p>
          <p className="text-xs text-gray-400 mt-1">点击右上"上传"添加一张图片或一段视频</p>
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {filtered.map((a) => {
            const thumb = a.thumbUrl || (a.type === "image" ? a.url : "");
            return (
              <div key={a.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="w-full h-32 bg-gray-100 relative">
                  {thumb ? (
                    <img src={thumb} alt={a.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <i className={a.type === "video" ? "ri-film-line text-3xl" : "ri-image-line text-3xl"} />
                    </div>
                  )}
                  <span className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                    a.type === "image" ? "bg-cyan-100 text-cyan-700" : "bg-fuchsia-100 text-fuchsia-700"
                  }`}>
                    {a.type === "image" ? "图" : "视频"}{a.durationSec > 0 ? ` ${fmtDuration(a.durationSec)}` : ""}
                  </span>
                  {a.usageCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-teal-600 text-white text-[10px] font-semibold">
                      引用 {a.usageCount}
                    </span>
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-xs font-semibold text-gray-900 truncate" title={a.title || a.fileName}>{a.title || a.fileName || "未命名"}</div>
                  <div className="text-[11px] text-gray-400 truncate mt-0.5">{fmtBytes(a.sizeBytes)} · {a.createdAt}</div>
                  {a.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {a.tags.slice(0, 3).map((t) => (
                        <span key={t} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px]">{t}</span>
                      ))}
                      {a.tags.length > 3 && <span className="text-[10px] text-gray-400">+{a.tags.length - 3}</span>}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 mt-2 text-xs">
                    <button onClick={() => setEditing(a)} className="text-gray-500 hover:text-teal-700">编辑</button>
                    <button
                      onClick={() => { replaceTargetRef.current = a.id; replaceRef.current?.click(); }}
                      className="text-gray-500 hover:text-teal-700"
                    >替换</button>
                    <a
                      href={a.type === "image" ? a.url : (a.thumbUrl || a.url)}
                      target="_blank" rel="noreferrer"
                      className="text-gray-500 hover:text-teal-700"
                    >预览</a>
                    <button onClick={() => onDelete(a)} className="text-red-500 hover:text-red-600 ml-auto">删除</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <EditModal asset={editing} onClose={() => setEditing(null)} onSave={onSaveEdit} />
      )}
    </div>
  );
}

function EditModal({ asset, onClose, onSave }: {
  asset: MediaAsset;
  onClose: () => void;
  onSave: (next: { title: string; description: string; tags: string[] }) => Promise<void>;
}) {
  const [title, setTitle] = useState(asset.title);
  const [description, setDescription] = useState(asset.description);
  const [tagsText, setTagsText] = useState(asset.tags.join(", "));
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const tags = tagsText.split(/[,，;；\s]+/).map((s) => s.trim()).filter(Boolean);
    try { await onSave({ title: title.trim(), description: description.trim(), tags }); }
    finally { setBusy(false); }
  };

  const previewUrl = asset.type === "image" ? asset.url : asset.thumbUrl || apiUrl(asset.url);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-gray-900">编辑媒体</h3>
        {previewUrl && (
          <div className="w-full h-40 bg-gray-100 rounded overflow-hidden">
            <img src={previewUrl} alt="" className="w-full h-full object-contain" />
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">标题</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">描述</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full h-20 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">标签（用空格 / 逗号分隔，搜索时按标签精确匹配）</label>
            <input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="如：三斗坪 上下通用 大坝远景" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">取消</button>
            <button type="submit" disabled={busy} className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60">{busy ? "保存中…" : "保存"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
